import React, { useContext, useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  InputAdornment,
  useMediaQuery,
  useTheme,
  Alert,
  MenuItem,
  Divider,
  IconButton
} from '@mui/material';
import { FaUserShield, FaEnvelope, FaPhone, FaBuilding, FaTimes } from 'react-icons/fa';
import { AuthContext } from '../../auth/AuthContext';
import { showSwalNotification } from '../../utils/SwalResponse';
import { useNavigate } from 'react-router-dom';
import LoadingComponent from '../../utils/LoadingComponent';

const countiesInKenya = [
  'Baringo', 'Bomet', 'Bungoma', 'Busia', 'Elgeyo-Marakwet', 'Embu', 'Garissa',
  'Homa Bay', 'Isiolo', 'Kajiado', 'Kakamega', 'Kericho', 'Kiambu', 'Kilifi',
  'Kirinyaga', 'Kisii', 'Kisumu', 'Kitui', 'Kwale', 'Laikipia', 'Lamu',
  'Machakos', 'Makueni', 'Mandera', 'Marsabit', 'Meru', 'Migori', 'Mombasa',
  'Murang\'a', 'Nairobi', 'Nakuru', 'Nandi', 'Narok', 'Nyamira', 'Nyandarua',
  'Nyeri', 'Samburu', 'Siaya', 'Taita-Taveta', 'Tana River', 'Tharaka-Nithi',
  'Trans Nzoia', 'Turkana', 'Uasin Gishu', 'Vihiga', 'Wajir', 'West Pokot'
];

const AdminRegistration = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { specialistRegistration, currentUser } = useContext(AuthContext);
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'specialist',
    organisation: '',
    county: '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [phoneError, setPhoneError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));

    if (name === 'email') {
      setEmailError(!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? 'Invalid email format' : '');
    }

    if (name === 'phone') {
      setPhoneError(!/^\+?\d{7,15}$/.test(value) ? 'Invalid phone number' : '');
    }
  };
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!currentUser) {
        navigate('/login');
      }
    }, 180000); // 3 minutes = 180000 milliseconds
  
    return () => clearTimeout(timeout); // Cleanup on unmount
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.name || !form.email || !form.role || !form.county) {
      return setError('Please fill all required fields.');
    }

    if (emailError || phoneError) {
      return setError('Please fix the validation errors.');
    }

    setLoading(true);

    try {
      const response = await specialistRegistration(form);
      showSwalNotification(response);
      if (response?.status === 'success') {
        setForm({
          name: '',
          email: '',
          phone: '',
          role: 'specialist',
          organisation: '',
          county: '',
        });
        navigate(-1);
      }
    } catch (err) {
      setError('Failed to register specialist. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <LoadingComponent loadingText="Registering..." />;

  
  return (
    <Box
      sx={{
        minHeight: '100vh',
        py: 6,
        px: 2,
        background: 'linear-gradient(145deg, #dcedc8 0%, #a5d6a7 100%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      {/* Back Button */}
      <Box sx={{ width: '100%', maxWidth: 520, mb: 2, display: 'flex', justifyContent: 'flex-start' }}>
        <IconButton
          onClick={() => navigate(-1)}
          sx={{
            color: 'red',
            border: '1.5px solid red',
            backgroundColor: '#ffffff',
            '&:hover': { backgroundColor: '#e8f5e9' },
          }}
        >
          <FaTimes />
        </IconButton>
      </Box>

      <Paper
        elevation={6}
        sx={{
          borderRadius: 5,
          maxWidth: 520,
          width: '100%',
          p: 4,
          background: '#f1f8e9',
          boxShadow: '0 8px 20px rgba(0,0,0,0.1)',
        }}
      >
        <Typography
          variant="h4"
          fontWeight="bold"
          textAlign="center"
          sx={{
            color: '#2e7d32',
            mb: 1,
            fontFamily: 'Playfair Display',
          }}
        >
          Register Specialist
        </Typography>

        <Typography
          variant="body2"
          textAlign="center"
          sx={{ color: '#4caf50', mb: 2 }}
        >
          Help us grow our environmental response network.
        </Typography>

        <Divider sx={{ mb: 3 }} />

        <form onSubmit={handleSubmit}>
          <Stack spacing={2}>
            <TextField
              label="Full Name"
              name="name"
              value={form.name}
              onChange={handleChange}
              required
              fullWidth
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FaUserShield color="#2e7d32" />
                  </InputAdornment>
                ),
              }}
              sx={{ bgcolor: '#ffffff', borderRadius: 2 }}
            />
            <TextField
              label="Email"
              name="email"
              value={form.email}
              onChange={handleChange}
              required
              fullWidth
              size="small"
              type="email"
              error={!!emailError}
              helperText={emailError}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FaEnvelope color="#1e88e5" />
                  </InputAdornment>
                ),
              }}
              sx={{ bgcolor: '#ffffff', borderRadius: 2 }}
            />
            <TextField
              label="Phone"
              name="phone"
              value={form.phone}
              onChange={handleChange}
              fullWidth
              size="small"
              type="tel"
              error={!!phoneError}
              helperText={phoneError}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FaPhone color="#388e3c" />
                  </InputAdornment>
                ),
              }}
              sx={{ bgcolor: '#ffffff', borderRadius: 2 }}
            />
            <TextField
              select
              label="Role"
              name="role"
              value={form.role}
              onChange={handleChange}
              required
              fullWidth
              size="small"
              SelectProps={{ native: true }}
              sx={{ bgcolor: '#ffffff', borderRadius: 2 }}
            >
              <option value="specialist">Specialist</option>
              {!!currentUser && currentUser?.role === 'support' && 
              <option value="admin">Authority</option>}
            </TextField>
            <TextField
              label="Organisation"
              name="organisation"
              value={form.organisation}
              onChange={handleChange}
              fullWidth
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FaBuilding color="#00796b" />
                  </InputAdornment>
                ),
              }}
              sx={{ bgcolor: '#ffffff', borderRadius: 2 }}
            />
            <TextField
              select
              label="County"
              name="county"
              value={form.county}
              onChange={handleChange}
              required
              fullWidth
              size="small"
              SelectProps={{ native: true }}
              sx={{ bgcolor: '#ffffff', borderRadius: 2 }}
            >
              <option value="" disabled>
                Select County
              </option>
              {countiesInKenya.map((county) => (
                <option key={county} value={county}>
                  {county}
                </option>
              ))}
            </TextField>
          </Stack>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          <Button
            type="submit"
            variant="contained"
            color="success"
            fullWidth
            sx={{
              mt: 4,
              py: 1.4,
              fontWeight: 'bold',
              fontSize: 16,
              borderRadius: 3,
              textTransform: 'none',
              background: 'linear-gradient(to right, #43a047, #66bb6a)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
              '&:hover': {
                background: 'linear-gradient(to right, #388e3c, #43a047)',
              },
            }}
            disabled={loading}
          >
            {loading ? 'Registering...' : 'Register Specialist'}
          </Button>
        </form>
      </Paper>
    </Box>
  );
};

export default AdminRegistration;
